import React from 'react'
import { styled } from 'baseui'
import type { StyleObject } from 'styletron-react'
import { lightTheme } from '@/customTheme'

/** Every named style in `lightTheme.typography` — the only sanctioned text sizes. */
export type TextVariant = keyof typeof lightTheme.typography

export type TextElement = 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label'

export interface TextHighlight {
  /** Substring to accent. The first occurrence is wrapped; a miss renders the text unchanged. */
  token: string
  color?: string
  /** Typography variant for the accented run — e.g. a bigger, bolder price inside a sentence. */
  variant?: TextVariant
}

export interface TextProps {
  /** Named typography token. Never pass raw font sizes — add a token to `customTheme` instead. */
  variant: TextVariant
  /**
   * Rendered element, default `'p'`. Not derivable from `variant`: heading level
   * is document structure, so the same token is an `h2` in one screen and a
   * `span` inside a sentence in another. Use `'span'` for inline runs.
   */
  as?: TextElement
  /** Overrides the variant's own color. */
  color?: string
  align?: 'left' | 'center' | 'right'
  /**
   * Accents one substring without splitting the translated sentence into
   * fragments. Only applies when `children` is a plain string.
   */
  highlight?: TextHighlight
  children?: React.ReactNode
}

function variantStyle(variant: TextVariant): StyleObject {
  return lightTheme.typography[variant] as StyleObject
}

const StyledText = styled(
  'p',
  (props: { $variant: TextVariant; $color?: string; $align?: TextProps['align'] }) => ({
    margin: 0,
    ...variantStyle(props.$variant),
    ...(props.$color ? { color: props.$color } : {}),
    ...(props.$align ? { textAlign: props.$align } : {}),
  }),
)

const HighlightRun = styled('span', (props: { $variant?: TextVariant; $color?: string }) => ({
  ...(props.$variant ? variantStyle(props.$variant) : {}),
  ...(props.$color ? { color: props.$color } : {}),
}))

/**
 * Typography primitive. Wraps `lightTheme.typography` so screens never hand-roll
 * `fontSize` / `fontWeight` / `lineHeight` on a raw element.
 *
 * Presentation only — no layout (margins stay 0, spacing belongs to `Stack`).
 */
function Text({ variant, as = 'p', color, align, highlight, children }: TextProps) {
  const content =
    highlight && typeof children === 'string' ? renderHighlighted(children, highlight) : children

  return (
    <StyledText $as={as} $variant={variant} $color={color} $align={align}>
      {content}
    </StyledText>
  )
}

function renderHighlighted(text: string, highlight: TextHighlight): React.ReactNode {
  const index = highlight.token ? text.indexOf(highlight.token) : -1
  if (index === -1) {
    return text
  }
  return (
    <>
      {text.slice(0, index)}
      <HighlightRun $variant={highlight.variant} $color={highlight.color}>
        {highlight.token}
      </HighlightRun>
      {text.slice(index + highlight.token.length)}
    </>
  )
}

export default Text
